import { inject, Injectable } from '@angular/core';
import { LogService } from '../logging/log.service';
import { HttpClient } from '@angular/common/http';
import { AppConfig } from 'app/config/service.config';
import { catchError, map, Observable, of, ReplaySubject, tap } from 'rxjs';
import { Pagination } from '../product/product.types';
import { ExportJobResponse, ExportJobStatus, TransactionResponse, TransactionStatus, TransactionType } from './transaction.types';

@Injectable({
	providedIn: 'root'
})
export class TransactionService {
	private _httpClient = inject(HttpClient);
	private _apiServer = inject(AppConfig);
	private _logging = inject(LogService);
	private apiUrl = this._apiServer.settings.apiServer.eastelService;

	private _transactions: ReplaySubject<TransactionResponse[]> = new ReplaySubject<TransactionResponse[]>(1);
	private _pagination: ReplaySubject<Pagination> = new ReplaySubject<Pagination>(1);

	constructor() { }

	// -----------------------------------------------------------------------------------------------------
	// @ Accessors
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Getter for transactions
	 */
	get transactions$(): Observable<TransactionResponse[]> {
		return this._transactions.asObservable();
	}

	/**
	 * Getter for pagination
	 */
	get pagination$(): Observable<Pagination> {
		return this._pagination.asObservable();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Public methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Get transactions
	 */
	getTransactions(params: {
		globalSearch: string | null;
		userId: string | null;
		memberId: string | null;
		phoneNumber: string | null;
		statuses: TransactionStatus[] | null;
		types: TransactionType[] | null;
		referenceId: string | null;
		referenceType: string | null;
		paymentMethod: string | string[] | null;
		channels: string[] | null;
		fromDate: string | null;
		toDate: string | null;
		page: number;
		size: number;
		sortBy: string;
		sortOrder: string;
	} = {
			globalSearch: null,
			userId: null,
			memberId: null,
			phoneNumber: null,
			statuses: null,
			types: null,
			referenceId: null,
			referenceType: null,
			paymentMethod: null,
			channels: null,
			fromDate: null,
			toDate: null,
			page: 0,
			size: 20,
			sortBy: 'createdAt',
			sortOrder: 'desc',
		}): Observable<TransactionResponse[]> {

		// Delete empty value
		Object.keys(params).forEach((key) => {
			if (Array.isArray(params[key])) {
				params[key] = params[key].filter((element) => element !== null);
			}
			if (
				params[key] === null ||
				params[key] === undefined ||
				params[key] === '' ||
				(Array.isArray(params[key]) && params[key].length === 0)
			) {
				delete params[key];
			}
		});

		const header = {
			params,
		};

		return this._httpClient.get<TransactionResponse[]>(`${this.apiUrl}/transactions/page`, header).pipe(
			map((response: any) => {
				if (response) {
					this._logging.debug('Transaction Service getTransactions', response);
					this._transactions.next(response.content);

					const pagination = {
						length: response.totalElements,
						size: response.size,
						page: response.number,
						lastPage: response.totalPages,
						startIndex: response.startIndex,
						endIndex: response.endIndex,
					};

					this._pagination.next(pagination);
					return response.content;
				}
			}),
			catchError((error) => {
				this._logging.error('Error getting transactions', error);
				return of([]);
			})
		);
	}

	/**
	 * Download transaction statement as XLSX
	 */
	downloadTransactionStatementExcel(params: {
		userId: string | null;
		memberId: string | null;
		phoneNumber: string | null;
		statuses: TransactionStatus[] | null;
		types: TransactionType[] | null;
		referenceId: string | null;
		referenceType: string | null;
		paymentMethod: string | string[] | null;
		channels: string[] | null;
		fromDate: string | null;
		toDate: string | null;
		sortBy: string;
		sortOrder: string;
	} = {
			userId: null,
			memberId: null,
			phoneNumber: null,
			statuses: null,
			types: null,
			referenceId: null,
			referenceType: null,
			paymentMethod: null,
			channels: null,
			fromDate: null,
			toDate: null,
			sortBy: 'createdAt',
			sortOrder: 'desc',
		}): Observable<Blob> {

		// Delete empty value
		Object.keys(params).forEach((key) => {
			if (Array.isArray(params[key])) {
				params[key] = params[key].filter((element) => element !== null);
			}
			if (
				params[key] === null ||
				params[key] === undefined ||
				params[key] === '' ||
				(Array.isArray(params[key]) && params[key].length === 0)
			) {
				delete params[key];
			}
		});

		const header = {
			params,
			responseType: 'blob' as 'json'
		};

		return this._httpClient.get(`${this.apiUrl}/transactions/statement/xlsx`, header).pipe(
			map((response: any) => {
				this._logging.debug('Transaction Service downloadTransactionStatementExcel', response);
				return response;
			}),
			catchError((error) => {
				this._logging.error('Error downloading transaction statement', error);
				throw error;
			})
		);
	}

	/**
	 * Request async Excel export job
	 */
	requestAsyncExport(params: {
		userId?: string | null;
		memberId?: string | null;
		phoneNumber?: string | null;
		statuses?: TransactionStatus[] | null;
		types?: TransactionType[] | null;
		referenceId?: string | null;
		referenceType?: string | null;
		paymentMethod?: string | string[] | null;
		channels?: string[] | null;
		fromDate?: string | null;
		toDate?: string | null;
	} = {}): Observable<ExportJobResponse> {
		const cleanParams: any = {};
		Object.keys(params).forEach((key) => {
			const val = params[key];
			if (Array.isArray(val)) {
				const filtered = val.filter(v => v !== null && v !== undefined);
				if (filtered.length > 0) cleanParams[key] = filtered;
			} else if (val !== null && val !== undefined && val !== '') {
				cleanParams[key] = val;
			}
		});

		return this._httpClient.post<ExportJobResponse>(
			`${this.apiUrl}/transactions/statement/xlsx/async`,
			null,
			{ params: cleanParams }
		).pipe(
			map((response) => {
				this._logging.debug('Transaction Service requestAsyncExport', response);
				return response;
			}),
			catchError((error) => {
				this._logging.error('Error requesting async export', error);
				throw error;
			})
		);
	}

	/**
	 * Poll async export job status
	 */
	getExportJobStatus(jobId: string): Observable<ExportJobResponse> {
		return this._httpClient.get<ExportJobResponse>(
			`${this.apiUrl}/transactions/statement/xlsx/jobs/${jobId}`
		).pipe(
			catchError((error) => {
				this._logging.error('Error polling export job status', error);
				throw error;
			})
		);
	}

	/**
	 * Download completed async export file
	 */
	downloadAsyncExportFile(jobId: string): Observable<Blob> {
		return this._httpClient.get(
			`${this.apiUrl}/transactions/statement/xlsx/jobs/${jobId}/download`,
			{ responseType: 'blob' }
		).pipe(
			catchError((error) => {
				this._logging.error('Error downloading async export file', error);
				throw error;
			})
		);
	}
}
